import { typeUtils } from '@festina-life/core'
import { FieldSet } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Icon } from '@festina-life/ui/structure'
import type { FormikProps } from 'formik'
import { useCallback, useEffect } from 'react'

import { AddBalancePostingFields } from './AddBalancePostingFields'
import { senderToReceiver } from './helpers'
import type { SingletonBalancePostingPair } from './types'

const validateKey = typeUtils.createValidator<SingletonBalancePostingPair>()

export const AddBalancePostingFieldsPair = ({
  values,
  setValues,
}: FormikProps<SingletonBalancePostingPair>) => {
  const translate = useTranslate()

  const handleAddReceiver = useCallback(
    () =>
      setValues((values) => ({
        ...values,
        receiver: senderToReceiver(values.sender, values.sender.accountType),
      })),
    [setValues],
  )

  const handleDeleteReceiver = useCallback(
    () =>
      setValues((values) => ({
        ...values,
        receiver: null,
      })),
    [setValues],
  )

  useEffect(() => {
    void setValues((newValues) => {
      if (newValues.receiver) {
        return {
          sender: newValues.sender,
          receiver: senderToReceiver(
            values.sender,
            newValues.receiver.accountType,
          ),
        }
      } else {
        return newValues
      }
    })
  }, [setValues, values.sender])

  return (
    <div className={values.receiver ? 'flex gap-2' : undefined}>
      <FieldSet
        label={
          values.receiver
            ? translate('postings.sender')
            : `${translate('postings.sender')} / ${translate('postings.receiver')}`
        }
        actions={[
          {
            key: 'add-receiver',
            onClick: handleAddReceiver,
            disabled: !!values.receiver,
            iconName: 'ArrowRightStartOnRectangle',
          },
        ]}
      >
        <AddBalancePostingFields
          fieldNamePrefix={validateKey('sender')}
          values={values.sender}
        />
      </FieldSet>
      {values.receiver && (
        <>
          <Icon name="ArrowRight" size="md" className="self-center" />
          <FieldSet
            label={translate('postings.receiver')}
            actions={[
              {
                key: 'delete-receiver',
                onClick: handleDeleteReceiver,
                iconName: 'Trash',
                isDanger: true,
              },
            ]}
          >
            <AddBalancePostingFields
              fieldNamePrefix={validateKey('receiver')}
              values={values.receiver}
              isReceiver
            />
          </FieldSet>
        </>
      )}
    </div>
  )
}
